(() => {
  'use strict';
  if (customElements.get('evilko-account')) return;
  const games = new Set(['all', 'wheel', 'crane', 'mahjong', 'bingo', 'duck', 'dig']);
  const statuses = new Set(['entered', 'active', 'paused', 'awaiting_draw', 'eliminated', 'won', 'not_won', 'expired', 'closed', 'ended', 'removed', 'caught', 'completed', 'fulfilled', 'ineligible', 'under_review']);
  const positiveStatuses = new Set(['won', 'fulfilled']);
  const activeStatuses = new Set(['entered', 'active', 'awaiting_draw', 'caught', 'completed']);
  const ongoingStatuses = new Set(['entered','active','paused','awaiting_draw','caught','completed','under_review']);
  const finishedStatuses = new Set(['won','fulfilled','not_won','eliminated','expired','closed','ended','removed','ineligible']);
  const count = value => typeof value === 'number' && Number.isFinite(value) && value >= 0;
  const optionalCount = value => value === null || count(value);

  class EvilkoAccount extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.game = 'all';
      this.view = 'overview';
      this.stateFilter = 'all';
      this.ledgerCursor = null;
      this.ledgerIds = new Set();
      this.cursor = null;
      this.sequence = 0;
      this.ids = new Set();
      this.hasAccount = false;
      this.accountKey = null;
      this.hasMore = false;
      this.lastMode = 'replace';
      this.clickHandler = event => this.onClick(event);
      this.addEventListener('click', this.clickHandler);
      this.changeHandler = event => {
        if (!event.target.matches('[data-etk-game-select]') || !games.has(event.target.value) || event.target.value === this.game) return;
        this.game = event.target.value;
        this.cursor = null;
        this.hasMore = false;
        this.ids.clear();
        this.el('entries').replaceChildren();
        this.el('empty').hidden = true;
        this.el('more').hidden = true;
        this.el('filter-note').hidden = true;
        this.load('replace');
      };
      this.keyHandler = event => { const tab = event.target.closest('[data-etk-tab]'); if (!tab || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return; const tabs = [...this.querySelectorAll('[data-etk-tab]')], index = tabs.indexOf(tab); const next = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length; event.preventDefault(); tabs[next].focus(); tabs[next].click(); };
      this.addEventListener('change', this.changeHandler);
      this.addEventListener('keydown', this.keyHandler);
      try {
        this.config = JSON.parse(this.querySelector('[data-etk-config]').textContent);
        this.copy = this.config.copy;
        this.numberFormat = new Intl.NumberFormat(this.config.locale || undefined);
        this.dateFormat = new Intl.DateTimeFormat(this.config.locale || undefined, { month: 'short', day: 'numeric', year: 'numeric' });
        this.timeFormat = new Intl.DateTimeFormat(this.config.locale || undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
        this.expiryFormat = new Intl.DateTimeFormat(this.config.locale || undefined, { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
        const endpoint = new URL(this.config.endpoint, location.origin);
        if (endpoint.origin !== location.origin || endpoint.pathname !== '/apps/evilko-wheel/account/state') throw new Error('Invalid account endpoint');
        this.endpoint = endpoint;
        this.focusHandler = () => { if (this.hasAccount && this.getAttribute('aria-busy') !== 'true') this.load('replace'); };
        window.addEventListener('focus', this.focusHandler);
        if (this.config.storefrontSignedIn === false && !this.config.designPreview) {
          this.signedOut();
          this.setAttribute('aria-busy', 'false');
        } else {
          this.load('replace');
        }
      } catch (_) {
        this.showInitialError();
      }
    }

    disconnectedCallback() {
      this.sequence += 1;
      if (this.controller) this.controller.abort();
      clearTimeout(this.timeout);
      this.removeEventListener('click', this.clickHandler);
      this.removeEventListener('change', this.changeHandler);
      this.removeEventListener('keydown', this.keyHandler);
      window.removeEventListener('focus', this.focusHandler);
      this.started = false;
    }

    el(name) { return this.querySelector(`[data-etk-${name}]`); }
    text(key, values = {}) {
      const value = this.copy && this.copy[key];
      return typeof value === 'string' ? value.replace(/\{\{\s*(\w+)\s*\}\}|\{(\w+)\}/g, (match, liquidName, name) => {
        const key = liquidName || name;
        return Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match;
      }) : '';
    }
    number(value) { return this.numberFormat.format(value); }
    date(value, withTime = false) {
      if ((typeof value !== 'string' && typeof value !== 'number') || value === '' || value === null) return '';
      if (typeof value === 'number' && !Number.isFinite(value)) return '';
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? '' : (withTime ? this.timeFormat : this.dateFormat).format(date);
    }
    safeUrl(value) {
      if (typeof value !== 'string' || !value || /[\u0000-\u0020\\]/.test(value)) return null;
      try {
        const url = new URL(value, location.origin);
        return url.origin === location.origin && /^https?:$/.test(url.protocol) && !url.username && !url.password ? url.pathname + url.search + url.hash : null;
      } catch (_) { return null; }
    }
    showInitialError() {
      this.setAttribute('aria-busy', 'false');
      this.el('loading').hidden = true;
      this.el('error').hidden = false;
    }
    resetPrivateData() {
      this.hasAccount = false;
      this.accountKey = null;
      this.cursor = null;
      this.hasMore = false;
      this.ids.clear();
      this.ledgerIds.clear();
      this.ledgerCursor = null;
      this.el('ledger-list').replaceChildren();
      this.el('dashboard').hidden = true;
      this.el('entries').replaceChildren();
      ['gold', 'silver', 'digs', 'bronze', 'green', 'green-tiers', 'green-held', 'held', 'allowance', 'reset', 'updated', 'notice', 'availability'].forEach(name => { this.el(name).textContent = ''; });
    }
    signedOut() {
      this.resetPrivateData();
      this.el('loading').hidden = true;
      this.el('error').hidden = true;
      this.el('signin').hidden = false;
    }
    isValid(data) {
      if (!data || data.version !== 1 || typeof data.signedIn !== 'boolean') return false;
      if (!data.signedIn) return true;
      if (typeof data.accountKey !== 'string' || !data.accountKey) return false;
      const balances = data.balances;
      const history = data.history;
      if (!balances || !balances.gold || !balances.silver || !balances.bronze || !history || !Array.isArray(history.items)) return false;
      return [balances.gold.available, balances.gold.held, balances.silver.remaining, balances.silver.allowance, balances.silver.used, balances.bronze.available].every(optionalCount) && typeof history.hasMore === 'boolean' && (!history.hasMore || typeof history.nextCursor === 'string' && history.nextCursor.length > 0);
    }
    busy(value, mode) {
      this.setAttribute('aria-busy', String(value));
      this.el('refresh').disabled = value;
      this.el('refresh').textContent = this.text(value ? 'refreshing' : 'refresh');
      this.el('more').disabled = value;
      this.el('ledger-more').disabled = value;
      this.el('ledger-more').textContent = this.text(value && mode === 'append' ? 'loading_more' : 'ledger_more');
      this.el('more').textContent = this.text(value && mode === 'append' ? 'loading_more' : 'more');
      this.el('retry').disabled = value;
      this.el('history-retry').disabled = value;
      this.el('history-loading').hidden = !(value && this.hasAccount && mode !== 'append');
    }
    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button) || button.disabled) return;
      if (button.hasAttribute('data-etk-tab')) {
        const view = button.dataset.etkTab;
        if (!['overview','entries','results','tokens'].includes(view) || this.view === view) return;
        this.view = view; this.stateFilter = view === 'results' ? 'completed' : 'all';
        this.syncView();
        if (view !== 'tokens') this.applyFilter();
        this.load('replace');
      } else if (button.hasAttribute('data-etk-state')) {
        const filter = button.dataset.etkState; if (!['all','active','completed'].includes(filter)) return;
        this.stateFilter = filter; this.applyFilter();
      } else if (button.hasAttribute('data-etk-ledger-more')) {
        if (this.ledgerCursor) this.load('append');
      } else if (button.hasAttribute('data-etk-game')) {
        const game = button.getAttribute('data-etk-game');
        if (!games.has(game) || game === this.game) return;
        this.game = game;
        this.cursor = null;
        this.hasMore = false;
        this.ids.clear();
        this.el('entries').replaceChildren();
        this.el('empty').hidden = true;
        this.el('more').hidden = true;
        this.querySelectorAll('[data-etk-game]').forEach(control => control.setAttribute('aria-pressed', String(control.getAttribute('data-etk-game') === game)));
        this.load('replace');
      } else if (button.hasAttribute('data-etk-more')) {
        if (this.hasMore && this.cursor) this.load('append');
      } else if (button.hasAttribute('data-etk-history-retry')) {
        this.load(this.lastMode);
      } else if (button.hasAttribute('data-etk-refresh') || button.hasAttribute('data-etk-retry')) {
        this.load('replace');
      }
    }
    async load(mode) {
      if (!this.endpoint) { this.showInitialError(); return; }
      if (this.config.designPreview && !this.config.storefrontSignedIn) { this.renderPreview(mode); return; }
      const sequence = ++this.sequence;
      if (this.controller) this.controller.abort();
      clearTimeout(this.timeout);
      this.controller = new AbortController();
      const controller = this.controller;
      this.lastMode = mode;
      this.busy(true, mode);
      this.el('error').hidden = true;
      this.el('signin').hidden = true;
      this.el('notice').hidden = true;
      this.el('history-error').hidden = true;
      if (!this.hasAccount) this.el('loading').hidden = false;
      const url = new URL(this.endpoint);
      url.searchParams.set('game', this.game);
      if (this.view === 'tokens') url.searchParams.set('view', 'tokens');
      const cursor = this.view === 'tokens' ? this.ledgerCursor : this.cursor;
      if (mode === 'append' && cursor) url.searchParams.set('cursor', cursor);
      this.timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url.pathname + url.search, { method: 'GET', credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal: controller.signal });
        if (sequence !== this.sequence) return;
        if (response.status === 401) { this.signedOut(); return; }
        const data = await response.json();
        if (sequence !== this.sequence) return;
        if (data && (data.error === 'sign_in_required' || data.code === 'sign_in_required')) { this.signedOut(); return; }
        if (mode === 'append' && response.status === 409 && data && (data.error === 'invalid_request' || data.code === 'invalid_request')) {
          this.resetPrivateData();
          this.load('replace');
          return;
        }
        if (!response.ok || !this.isValid(data)) throw new Error('Account unavailable');
        if (!data.signedIn) { this.signedOut(); return; }
        if (this.accountKey && this.accountKey !== data.accountKey) {
          this.resetPrivateData();
          if (mode === 'append') { this.load('replace'); return; }
        }
        this.render(data, mode);
      } catch (_) {
        if (sequence !== this.sequence) return;
        if (!this.hasAccount) {
          this.showInitialError();
        } else {
          this.el('notice').textContent = this.text('refresh_error');
          this.el('notice').hidden = false;
          this.el('history-error').hidden = false;
          this.el('empty').hidden = true;
        }
      } finally {
        if (sequence === this.sequence) {
          clearTimeout(this.timeout);
          this.el('loading').hidden = true;
          this.busy(false, mode);
        }
      }
    }
    render(data, mode) {
      this.accountKey = data.accountKey;
      this.hasAccount = true;
      this.el('dashboard').hidden = false;
      this.el('error').hidden = true;
      this.el('signin').hidden = true;
      const { gold, silver, bronze } = data.balances;
      const green=data.balances.green||{available:null,held:null,tiers:[]};
      [['gold', gold.available], ['silver', count(silver.remaining) ? Math.floor(silver.remaining / 50) : null], ['bronze', bronze.available], ['green', green.available]].forEach(([name, value]) => {
        this.el(name).textContent = count(value) ? this.number(value) : this.text('balance_unknown');
        this.el(name).classList.toggle('etk-balance-unavailable', !count(value));
      });
      this.el('digs').textContent = count(silver.remaining) ? this.text('silver_digs', {count:this.number(silver.remaining)}) : '';
      this.el('green-held').hidden = !count(green.held) || green.held===0;
      this.el('green-held').textContent = count(green.held)&&green.held>0?this.text('green_held',{count:this.number(green.held)}):'';
      this.el('green-tiers').replaceChildren();
      for(const tier of Array.isArray(green.tiers)?green.tiers:[]){
        if(tier && typeof tier.title==='string' && count(tier.available) && tier.available>0)this.el('green-tiers').append(this.node('li','',this.text('green_tier',{tier:tier.title,count:this.number(tier.available)})));
      }
      this.el('held').hidden = !count(gold.held) || gold.held === 0;
      this.el('held').textContent = count(gold.held) && gold.held > 0 ? this.text('gold_held', { count: this.number(gold.held) }) : '';
      this.el('allowance').textContent = count(silver.used) && count(silver.allowance) ? this.text('silver_allowance', { used: this.number(silver.used), allowance: this.number(silver.allowance) }) : '';
      const resetDate = (typeof silver.nextMonthAt === 'string' && silver.nextMonthAt || typeof silver.nextMonthAt === 'number' && Number.isFinite(silver.nextMonthAt)) ? new Date(silver.nextMonthAt) : null;
      const reset = resetDate && !Number.isNaN(resetDate.getTime()) ? this.expiryFormat.format(resetDate) : ''; 
      this.el('reset').hidden = !reset || !count(silver.allowance);
      this.el('reset').textContent = reset ? this.text('silver_reset', { date: reset }) : '';
      const updated = this.date(data.serverTime, true);
      this.el('updated').textContent = updated ? this.text('updated', { date: updated }) : '';
      this.syncView();
      if (this.view === 'tokens') { this.renderLedger(data.tokenHistory, mode); return; }
      if (mode !== 'append') {
        this.ids.clear();
        this.el('entries').replaceChildren();
      }
      const fragment = document.createDocumentFragment();
      data.history.items.forEach(item => {
        if (!item || (typeof item.id !== 'string' && typeof item.id !== 'number')) return;
        const key = `${item.type}:${item.id}`;
        if (this.ids.has(key)) return;
        this.ids.add(key);
        fragment.append(this.entry(item));
      });
      this.el('entries').append(fragment);
      this.cursor = data.history.nextCursor || null;
      this.hasMore = data.history.hasMore && !!this.cursor;
      this.el('more').hidden = !this.hasMore;
      const availability = data.availability || {};
      const unavailable = this.game === 'all' ? ['wheel', 'crane', 'mahjong', 'bingo', 'dig'].some(game => availability[game] === false) : availability[this.game] === false;
      this.el('availability').hidden = !unavailable;
      this.el('availability').textContent = unavailable ? this.text('records_unavailable') : '';
      const empty = this.ids.size === 0;
      this.el('empty').hidden = !empty;
      this.el('empty-title').textContent = this.text(unavailable ? 'empty_unavailable_title' : this.game === 'all' ? 'empty_title' : 'empty_filter_title');
      this.el('empty-text').textContent = this.text(unavailable ? 'empty_unavailable_text' : this.game === 'all' ? 'empty_text' : 'empty_filter_text');
      this.applyFilter();
    }
    node(tag, className, text) {
      const node = document.createElement(tag);
      if (className) node.className = className;
      if (typeof text === 'string') node.textContent = text;
      return node;
    }
    syncView() {
      this.querySelectorAll('[data-etk-tab]').forEach(tab => { const selected = tab.dataset.etkTab === this.view; tab.setAttribute('aria-selected', String(selected)); tab.tabIndex = selected ? 0 : -1; if (selected) this.el('panel').setAttribute('aria-labelledby', tab.id); });
      this.el('balance-section').hidden = this.view !== 'overview';
      this.el('game-section').hidden = this.view === 'tokens';
      this.el('ledger').hidden = this.view !== 'tokens';
      this.el('earn-section').hidden = this.view !== 'overview';
      this.querySelector('.etk-state-filters').hidden = this.view === 'results';
      this.querySelector('[data-etk-game-select]').value = this.game;
    }
    applyFilter() {
      let visible = 0;
      this.el('entries').querySelectorAll('.etk-entry').forEach(row => { const status = row.dataset.status; const show = this.stateFilter === 'all' || (this.stateFilter === 'active' ? ongoingStatuses.has(status) : finishedStatuses.has(status)); row.hidden = !show; if (show) visible++; });
      this.querySelectorAll('[data-etk-state]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.etkState === this.stateFilter)));
      this.el('filter-note').hidden = !this.hasMore || this.stateFilter === 'all';
      this.el('empty').hidden = visible > 0;
      if (visible === 0 && this.stateFilter !== 'all') { this.el('empty-title').textContent = this.text('filtered_empty_title'); this.el('empty-text').textContent = this.text('filtered_empty_text'); }
      else if (visible === 0 && this.ids.size === 0 && this.el('availability').hidden) { this.el('empty-title').textContent = this.text(this.game === 'all' ? 'empty_title' : 'empty_filter_title'); this.el('empty-text').textContent = this.text(this.game === 'all' ? 'empty_text' : 'empty_filter_text'); }
    }
    gameIcon(type) {
      const shapes = {wheel:'<circle cx="12" cy="12" r="9"/><path d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M5.6 18.4 18.4 5.6"/>',mahjong:'<rect x="3" y="2" width="8" height="10" rx="1"/><rect x="13" y="12" width="8" height="10" rx="1"/><path d="M6 5h2M6 8h2M16 15h2M16 18h2"/>',bingo:'<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 3v18M15 3v18M3 9h18M3 15h18"/>',dig:'<path d="m12 2 9 5v10l-9 5-9-5V7Zm0 10L3 7m9 5 9-5m-9 5v10"/>',crane:'<path d="M3 3h18M12 3v8M6 13l6-3 6 3-2 7-4 2-4-2Z"/>',duck:'<path d="M4 13c0 6 13 8 16 0l-7-1V6a3 3 0 1 0-6 0v7Z"/>'};
      const icon = this.node('span','etk-game-icon'); icon.setAttribute('aria-hidden','true'); icon.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">'+(shapes[type]||shapes.wheel)+'</svg>'; return icon;
    }
    entry(item) {
      const row = this.node('li', 'etk-entry'), type = games.has(item.type) && item.type !== 'all' ? item.type : null;
      const status = statuses.has(item.status) ? item.status : 'unknown'; row.dataset.status = status;
      const main = this.node('div', 'etk-entry-main'), name = this.node('div','etk-entry-name');
      name.append(this.node('h3','',this.text(type || 'history_title')));
      if (typeof item.title === 'string' && item.title && item.title !== this.text(type)) name.append(this.node('p','etk-game-subtitle',item.title));
      main.append(this.gameIcon(type),name);
      const details = this.node('div','etk-entry-details');
      if (count(item.quantity)) { const unit = item.unit === 'entries' ? 'entry' : String(item.unit||'').replace(/s$/, ''); if (['entry','drop','block','dig','pair'].includes(unit)) details.append(this.node('span','',this.text(unit === 'entry' && item.quantity !== 1 ? 'quantity_entries' : 'quantity_'+unit+(item.quantity === 1 ? '' : 's'),{count:this.number(item.quantity)}))); }
      if (typeof item.prizeTitle === 'string' && item.prizeTitle) details.append(this.node('span','etk-prize',item.prizeTitle));
      const entered=this.date(item.enteredAt); details.append(this.node('small','etk-entry-date',entered || this.text('date_unknown')));
      if (item.gameRemoved === true) details.append(this.node('small','etk-removed',this.text('game_removed')));
      const outcome=this.node('div','etk-entry-status'); outcome.append(this.node('span','etk-status'+(positiveStatuses.has(status)?' etk-status-win':ongoingStatuses.has(status)?' etk-status-active':''),this.text('status_'+status)));
      if (Number.isInteger(item.place)&&item.place>=1&&item.place<=3) outcome.append(this.node('small','etk-place',this.text('prize_place',{place:this.number(item.place)})));
      const action=this.node('div','etk-entry-action'),url=this.safeUrl(item.url);
      if (url && type !== 'duck' && item.gameRemoved !== true && status !== 'removed') { const link=this.node('a','etk-entry-link',this.text(type==='wheel'?'view_wheel':'view_game')); link.href=url; const arrow=this.node('span','','→');arrow.setAttribute('aria-hidden','true');link.append(arrow);action.append(link); }
      row.append(main,details,outcome,action);return row;
    }
    renderLedger(history, mode) {
      const valid = history && history.available === true && Array.isArray(history.items);
      if (mode !== 'append' || !valid) { this.el('ledger-list').replaceChildren(); this.ledgerIds.clear(); }
      this.el('ledger-notice').hidden = valid && !history.partial;
      this.el('ledger-notice').textContent = !valid ? this.text('ledger_unavailable') : history.partial ? this.text('ledger_partial') : ''; 
      for (const item of valid ? history.items : []) {
        if (!item || typeof item.id !== 'string' || this.ledgerIds.has(item.id) || !['gold','silver','bronze','green'].includes(item.type) || !Number.isFinite(item.amount) || !['change','allowance'].includes(item.effect)) continue;
        this.ledgerIds.add(item.id);
        const row=this.node('li','etk-ledger-row'),description=this.node('div','etk-ledger-description');
        description.append(this.node('strong','',this.text('ledger_'+item.type)));
        const reason=this.copy['ledger_reason_'+item.reason] ? this.text('ledger_reason_'+item.reason) : typeof item.reason === 'string' && item.reason.trim() ? item.reason.slice(0,180) : this.text('ledger_reason_activity');
        description.append(this.node('span','',reason));
        const amount=item.effect==='allowance'?this.text('ledger_allowance',{count:this.number(item.amount)}):this.text('ledger_change',{count:(item.amount>0?'+':'')+this.number(item.amount),unit:this.text(item.unit==='digs'?'ledger_digs':'ledger_tokens')});
        row.append(description,this.node('strong', 'etk-ledger-amount'+(item.amount>0&&item.effect==='change'?' is-credit':''),amount),this.node('time','',this.date(item.createdAt,true)));this.el('ledger-list').append(row);
      }
      this.ledgerCursor=valid && history.hasMore && typeof history.nextCursor==='string'?history.nextCursor:null;
      this.el('ledger-more').hidden=!this.ledgerCursor;
      this.el('ledger-empty').hidden=!valid || this.ledgerIds.size>0;
    }
    renderPreview(mode) {
      const now=Date.now();
      const items=[{id:'preview-wheel',type:'wheel',title:'Grand Prize',prizeTitle:'Grand Prize draw',status:'entered',enteredAt:now,quantity:1,unit:'entry',url:'/pages/prize-wheel'},{id:'preview-tiles',type:'mahjong',title:'Tiles',prizeTitle:'Registration complete',status:'entered',enteredAt:now,quantity:0,unit:'pair',url:'/pages/mahjong-match'},{id:'preview-bingo',type:'bingo',title:'Bingo',prizeTitle:'Green token awarded',status:'won',enteredAt:now,quantity:1,unit:'entry',url:'/pages/bingo'}].filter(item=>this.game==='all'||item.type===this.game);
      this.el('loading').hidden=true;this.el('preview').hidden=false;
      this.render({version:1,signedIn:true,accountKey:'theme-preview',serverTime:now,balances:{gold:{available:3,held:0},silver:{remaining:100,allowance:100,used:0,nextMonthAt:null},bronze:{available:4},green:{available:1,held:0,tiers:[]}},history:{items,hasMore:false,nextCursor:null},tokenHistory:{available:true,items:[{id:'preview-earned',type:'green',amount:1,unit:'tokens',reason:'award',effect:'change',createdAt:now},{id:'preview-digs',type:'silver',amount:100,unit:'digs',reason:'monthly',effect:'allowance',createdAt:now}],hasMore:false,nextCursor:null}},mode);
      this.busy(false,mode);
    }

  }
  customElements.define('evilko-account', EvilkoAccount);
})();
